import { useEffect, useRef } from 'react';
import { bibleState } from '@/state/bible-state';
import { desktopUpdateStore } from './desktop-update-state';
import { getDesktop, isDesktop } from './desktop';
import type { DesktopUpdateState } from './skjv-desktop';

/**
 * Desktop auto-update UX: the shell checks quietly; when an update is
 * available the house ConfirmDialog makes the offer, and installing is
 * always the member's click. The download then lives in the persistent
 * update toast (DesktopUpdateToast) — progress, then the restart prompt;
 * the app never restarts itself. Failures surface as an honest toast —
 * the shell already refused the artifact (fail-closed), nothing is
 * half-done.
 */
export function useDesktopUpdates(): void {
  const offeredVersion = useRef<string | null>(null);
  const lastPhase = useRef<DesktopUpdateState['phase']>('idle');

  useEffect(() => {
    if (!isDesktop()) return;
    const updates = getDesktop()?.updates;
    if (!updates) return;

    updates.onState((state) => {
      const phaseChanged = state.phase !== lastPhase.current;
      lastPhase.current = state.phase;
      desktopUpdateStore.setState(state);

      if (state.phase === 'available' && state.version !== offeredVersion.current) {
        offeredVersion.current = state.version;
        bibleState.showConfirmDialog({
          title: 'Update ready',
          message: `SKJV Bible Study ${state.version} is available — you have ${state.current}. Download it now?`,
          confirmText: 'Download',
          cancelText: 'Later',
          onConfirm: () => void updates.install(),
        });
      } else if (state.phase === 'error' && phaseChanged && state.error) {
        // Allow the same version to be offered again after a refusal.
        offeredVersion.current = null;
        bibleState.showToast(`The update was not installed: ${state.error}`, 'error');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
